import clsx from 'clsx';
import * as React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { FRAGMENT_ANNOTATION_BODY } from '@gitbook/doc-core';
import { type Change, type Fragment, isTextRangeSelection, TextRange } from '@gitbook/slate';
import { FragmentRenderer, type InlineProps, ToggleFragmentEditing } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Form,
    Popover,
    PopoverRoot,
    PopoverTrigger,
    Submit,
    Toolbar,
} from '@gitbook/spine-react';

import {
    getInlineText,
    InlineCard,
    InlineEditingContext,
    InlineTextField,
    setInlineText,
    useInlineEdit,
    useInlineTagProps,
    useIsInlineActive,
    useIsInlineActiveBySelection,
} from '../inlines';

/**
 * Thin performant wrapper that renders an inline annotation.
 */
export function InlineAnnotation(props: InlineProps) {
    const { children, attributes } = props;
    const [isActive, inlineProps] = useIsInlineActive(props);

    const mark = (
        <AnnotationTag {...attributes} {...inlineProps}>
            {children}
        </AnnotationTag>
    );

    if (isActive) {
        if (props.editor.readOnly) {
            return <ReadOnlyAnnotation {...props}>{mark}</ReadOnlyAnnotation>;
        }
        return <EditableAnnotation {...props}>{mark}</EditableAnnotation>;
    }

    return mark;
}

/**
 * Read-only annotation.
 */
function ReadOnlyAnnotation(
    props: Pick<InlineProps, 'node'> & {
        children: React.ReactElement;
    }
) {
    const { children, node } = props;
    const fragment = node.assertFragment(FRAGMENT_ANNOTATION_BODY);

    return (
        <PopoverRoot triggerAction="hover">
            <PopoverTrigger>{children}</PopoverTrigger>
            <Popover>
                <InlineCard label="Annotation preview">
                    <ReadOnlyAnnotationFragment fragment={fragment} />
                </InlineCard>
            </Popover>
        </PopoverRoot>
    );
}

/**
 * Editable annotation.
 */
function EditableAnnotation(
    props: Pick<InlineProps, 'node' | 'editor' | 'isSelected'> & {
        children: React.ReactElement;
    }
) {
    const { node, editor, children } = props;
    const fragment = node.assertFragment(FRAGMENT_ANNOTATION_BODY);

    const isFragmentFocused = editor.value.selection.hasFocusIn(fragment);
    const isActiveBySelection = useIsInlineActiveBySelection(props);
    const isOnlyNodeSelected = isActiveBySelection && !isFragmentFocused;
    const [editedFragment, setEditedFragment] = React.useState<Fragment | null>(
        isFragmentFocused ? fragment : null
    );
    const isEditing = Boolean(editedFragment);
    const [isManuallyOpen, setIsOpen] = React.useState(isFragmentFocused);
    const isOpen = isManuallyOpen || isOnlyNodeSelected;

    const inlineEdit = useInlineEdit({
        editor,
        node,
        isOpen,
        isEditing,
        onOpenChange: setIsOpen,
        onCancel: () => {
            if (editedFragment) {
                editor.change((change) => {
                    change.replaceNodeByKey(fragment.key, editedFragment);
                });
            }
        },
        onEdit: (isEditing) => {
            if (isEditing) {
                setEditedFragment(fragment);
                editor.change((change) => focusFragment(change, fragment));
            } else {
                setEditedFragment(null);
            }
        },
    });

    return (
        <PopoverRoot {...inlineEdit.popoverRootProps}>
            <InlineEditingContext.Provider value={isEditing && isOpen}>
                <PopoverTrigger>{children}</PopoverTrigger>
            </InlineEditingContext.Provider>
            <Popover {...inlineEdit.popoverProps}>
                {isEditing ? (
                    <InlineCard label="Annotation edition" className="flex flex-col gap-3">
                        <AnnotationForm editor={editor} node={node} onSubmit={inlineEdit.submit} />
                    </InlineCard>
                ) : (
                    <InlineCard label="Annotation preview" className="flex gap-2 pr-2">
                        <div className="flex flex-1">
                            <ReadOnlyAnnotationFragment fragment={fragment} />
                        </div>
                        <Toolbar
                            unstyled
                            aria-label="Actions"
                            size="small"
                            orientation="vertical"
                            className="gap-0.5"
                        >
                            <Button
                                size="small"
                                kind="blank"
                                iconOnly
                                leadingIcon={Icon.Edit}
                                label="Edit"
                                onPress={inlineEdit.edit}
                            />
                            <Button
                                size="small"
                                kind="blank"
                                iconOnly
                                leadingIcon={Icon.ClearCircle}
                                label="Remove"
                                onPress={() => {
                                    editor.change((change) => {
                                        change.unwrapInlineByKey(node.key);
                                    });
                                }}
                            />
                        </Toolbar>
                    </InlineCard>
                )}
            </Popover>
        </PopoverRoot>
    );
}

function ReadOnlyAnnotationFragment(props: { fragment: Fragment }) {
    const { fragment } = props;

    if (fragment.getText().length > 0) {
        return (
            <ToggleFragmentEditing readOnly>
                <FragmentRenderer fragment={FRAGMENT_ANNOTATION_BODY} />
            </ToggleFragmentEditing>
        );
    }

    return <span className="text-muted italic">Empty annotation</span>;
}

type AnnotationFormValues = {
    text: string;
};

function AnnotationForm(props: Pick<InlineProps, 'editor' | 'node'> & { onSubmit: () => void }) {
    const { editor, node } = props;
    const form = useForm<AnnotationFormValues>({
        defaultValues: { text: getInlineText(node) },
    });
    const onSubmit: SubmitHandler<AnnotationFormValues> = ({ text }) => {
        editor.change((change) => setInlineText(change, node, text));
        props.onSubmit();
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <InlineTextField name="text" />
            <div className="flex rounded border p-2">
                <FragmentRenderer fragment={FRAGMENT_ANNOTATION_BODY} />
            </div>
            <Submit
                className="self-end"
                kind="blank"
                leadingIcon={Icon.CheckCircle}
                label="Apply"
                size="small"
                disabled={false}
            />
        </Form>
    );
}

/**
 * Focus a fragment.
 */
function focusFragment(change: Change, fragment: Fragment) {
    if (!isTextRangeSelection(change.value.selection)) {
        change
            .selectText(TextRange.fromBlockRange(change.value.selection, change.value.document))
            .collapseToEndOf(fragment)
            .focus();
    } else {
        change.collapseToEndOf(fragment).focus();
    }
}

/**
 * Low level annotation tag.
 */
const AnnotationTag = React.forwardRef(function AnnotationTag(
    props: React.ComponentPropsWithoutRef<'span'>,
    ref: React.ForwardedRef<HTMLSpanElement>
) {
    const tagProps = useInlineTagProps(props);
    return (
        <span
            ref={ref}
            className={clsx(
                'underline decoration-2 decoration-muted decoration-dotted',
                tagProps.className
            )}
            {...tagProps}
        />
    );
});
